import { useState, useCallback, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Row, Col, Card, CardBody, Input } from "reactstrap";
import CertificateSvg from "./CertificateSvg";

/* ─────────────────────────────────────────────────────────────
   STRATEGY: Use html2canvas with a hidden, fully-styled iframe
   clone so web fonts are loaded before capture. Then use jsPDF
   to embed the high-res canvas as a landscape A4 certificate.
───────────────────────────────────────────────────────────── */

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=Cinzel:wght@400;600;700&family=Great+Vibes&display=swap');

  *, *::before, *::after { box-sizing: border-box; }

  body { margin: 0; background: #fff; min-height: 100vh; }

  .app-shell {
    min-height: 100vh;
    background: radial-gradient(ellipse 120% 80% at 50% 0%, #1c1508 0%, #0e0c08 100%);
    padding: 2.5rem 1rem;
    font-family: 'Cormorant Garamond', serif;
  }

  /* ── Certificate card ── */
  .cert-card {
    background: linear-gradient(160deg, #fdfbf2 0%, #f8f2d8 40%, #f2e8bf 100%);
    border: none !important;
    border-radius: 4px !important;
    position: relative;
    box-shadow:
      inset 0 0 60px rgba(180,140,40,0.08),
      0 0 0 1px #b8941e,
      0 0 0 5px #0e0c08,
      0 0 0 7px #b8941e,
      0 0 0 13px #0e0c08,
      0 40px 100px rgba(0,0,0,0.85);
    overflow: hidden;
    max-width: 900px;
    margin: 0 auto;
  }

  /* Parchment texture overlay */
  .cert-card::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      repeating-linear-gradient(0deg, transparent, transparent 28px, rgba(180,140,40,0.04) 28px, rgba(180,140,40,0.04) 29px),
      repeating-linear-gradient(90deg, transparent, transparent 28px, rgba(180,140,40,0.03) 28px, rgba(180,140,40,0.03) 29px);
    pointer-events: none;
    z-index: 0;
  }

  .cert-inner {
    position: relative;
    z-index: 1;
    padding: 2.5rem 3.5rem;
    border: 1.5px solid rgba(184,148,30,0.35);
    margin: 18px;
  }

  /* ── Corner ornaments ── */
  .corner { position: absolute; width: 64px; height: 64px; z-index: 4; }
  .corner svg { width: 100%; height: 100%; }
  .corner-tl { top: 12px; left: 12px; }
  .corner-tr { top: 12px; right: 12px; transform: scaleX(-1); }
  .corner-bl { bottom: 12px; left: 12px; transform: scaleY(-1); }
  .corner-br { bottom: 12px; right: 12px; transform: scale(-1); }

  /* ── Medallion ── */
  .medallion { width: 80px; height: 80px; margin: 0 auto 0.75rem; }
  .medallion-outer {
    width: 80px; height: 80px; border-radius: 50%;
    background: conic-gradient(
      #c9a227 0deg, #f0d060 30deg, #c9a227 60deg, #f0d060 90deg,
      #c9a227 120deg, #f0d060 150deg, #c9a227 180deg, #f0d060 210deg,
      #c9a227 240deg, #f0d060 270deg, #c9a227 300deg, #f0d060 330deg, #c9a227 360deg
    );
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 0 0 3px #7a5c0a, 0 4px 20px rgba(0,0,0,0.4);
  }
  .medallion-inner {
    width: 58px; height: 58px; border-radius: 50%;
    background: radial-gradient(circle at 35% 35%, #d4a82a, #7a5c0a);
    display: flex; align-items: center; justify-content: center;
    font-size: 24px; color: #fdf6d0;
    text-shadow: 0 1px 4px rgba(0,0,0,0.5);
  }

  /* ── Typography ── */
  .cert-institution {
    font-family: 'Cinzel', serif; font-size: 0.65rem;
    font-weight: 600; letter-spacing: 0.45em;
    color: #7a5c0a; text-transform: uppercase;
  }
  .divider-ornament {
    display: flex; align-items: center; gap: 10px;
    margin: 0.6rem 0; color: #c9a227; font-size: 0.85rem;
  }
  .divider-ornament::before, .divider-ornament::after {
    content: ''; flex: 1; height: 1px;
    background: linear-gradient(to right, transparent, #c9a227 40%, #c9a227 60%, transparent);
  }
  .cert-headline {
    font-family: 'Cinzel', serif; font-weight: 700;
    font-size: 2.6rem; color: #2a1d00;
    letter-spacing: 0.1em; line-height: 1;
  }
  .cert-of {
    font-family: 'Cormorant Garamond', serif; font-style: italic;
    font-size: 1.2rem; font-weight: 300; color: #7a5c0a; letter-spacing: 0.2em;
  }
  .cert-presented {
    font-family: 'Cormorant Garamond', serif; font-style: italic;
    font-size: 0.95rem; color: #5a4010; letter-spacing: 0.08em;
  }
  .cert-recipient {
    font-family: 'Great Vibes', cursive; font-size: 3.4rem;
    color: #1a1200; line-height: 1.15;
    text-shadow: 0 1px 0 rgba(200,160,40,0.2); min-height: 3.8rem;
  }
  .cert-body-text {
    font-family: 'Cormorant Garamond', serif; font-size: 1rem;
    font-weight: 400; color: #3d2d00; line-height: 1.7;
    max-width: 520px; margin: 0 auto;
  }
  .cert-course-title {
    font-family: 'Cinzel', serif; font-size: 0.95rem;
    font-weight: 600; color: #7a5c0a; letter-spacing: 0.1em;
  }

  /* ── Signature section ── */
  .sig-block { text-align: center; }
  .sig-value {
    font-family: 'Great Vibes', cursive; font-size: 1.7rem;
    color: #1a1200; line-height: 1; min-height: 2rem;
  }
  .sig-line { border: none; border-top: 1px solid rgba(90,64,16,0.5); margin: 4px auto; width: 140px; }
  .sig-name { font-family: 'Cinzel', serif; font-size: 0.58rem; letter-spacing: 0.18em; color: #5a4010; text-transform: uppercase; }
  .sig-role { font-family: 'Cormorant Garamond', serif; font-size: 0.75rem; font-style: italic; color: #8b6914; }
  .cert-id { font-family: 'Cinzel', serif; font-size: 0.58rem; letter-spacing: 0.15em; color: rgba(90,64,16,0.6); }

  /* ── Controls ── */
  .controls {
    max-width: 900px; margin: 2rem auto 0;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(201,162,39,0.18);
    border-radius: 10px; padding: 1.5rem;
  }
  .ctrl-label {
    font-family: 'Cinzel', serif; font-size: 0.58rem;
    letter-spacing: 0.2em; text-transform: uppercase;
    color: #c9a227; margin-bottom: 4px;
  }
  .ctrl-input {
    background: rgba(255,255,255,0.05) !important;
    border: 1px solid rgba(201,162,39,0.25) !important;
    color: #f2e8bf !important; border-radius: 5px !important;
    font-size: 0.88rem !important;
    font-family: 'Cormorant Garamond', serif !important;
    padding: 0.4rem 0.7rem !important;
  }
  .ctrl-input:focus {
    border-color: #c9a227 !important;
    box-shadow: 0 0 0 2px rgba(201,162,39,0.18) !important;
    background: rgba(255,255,255,0.08) !important;
    color: #fdf6d0 !important; outline: none !important;
  }
  .ctrl-input::placeholder { color: rgba(242,232,191,0.25) !important; }

  .btn-gold {
    background: linear-gradient(135deg, #b8941e, #e0b830, #b8941e);
    border: none; color: #0e0c08;
    font-family: 'Cinzel', serif; font-size: 0.7rem;
    letter-spacing: 0.2em; text-transform: uppercase;
    padding: 0.6rem 1.8rem; border-radius: 4px;
    font-weight: 600; cursor: pointer; transition: all 0.2s;
    display: inline-flex; align-items: center; gap: 8px;
  }
  .btn-gold:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(201,162,39,0.35); }
  .btn-gold:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

  .section-heading {
    font-family: 'Cinzel', serif; font-size: 0.62rem;
    letter-spacing: 0.3em; color: #c9a227;
    text-transform: uppercase; margin-bottom: 1rem; opacity: 0.8;
  }

  /* Hidden PDF render target — full quality, off-screen */
  .pdf-render-target {
    position: fixed;
    top: -9999px;
    left: -9999px;
    width: 1122px;   /* A4 landscape px at 96dpi */
    background: #fff;
    z-index: -1;
    pointer-events: none;
    visibility: hidden;
  }

  /* Styles for the PDF render target (landscape-optimised) */
  .pdf-cert-card {
    background: linear-gradient(160deg, #fdfbf2 0%, #f8f2d8 40%, #f2e8bf 100%);
    position: relative;
    width: 1122px;
    min-height: 793px;
    overflow: visible;
    font-family: 'Cormorant Garamond', serif;
  }
  .pdf-cert-inner {
    position: relative; z-index: 1;
    padding: 48px 72px 40px;
    border: 2px solid rgba(184,148,30,0.4);
    margin: 24px;
    min-height: 745px;
  }
  .pdf-parchment {
    position: absolute; inset: 0; pointer-events: none; z-index: 0;
    background-image:
      repeating-linear-gradient(0deg, transparent, transparent 28px, rgba(255, 255, 255, 1) 28px, rgba(255, 255, 255, 1) 29px),
      repeating-linear-gradient(90deg, transparent, transparent 28px, rgba(255, 255, 255, 1) 28px, rgba(255, 255, 255, 1) 29px);
  }
  .pdf-corner { position: absolute; width: 80px; height: 80px; z-index: 4; }
  .pdf-corner svg { width: 100%; height: 100%; }
  .pdf-corner-tl { top: 8px; left: 8px; }
  .pdf-corner-tr { top: 8px; right: 8px; transform: scaleX(-1); }
  .pdf-corner-bl { bottom: 8px; left: 8px; transform: scaleY(-1); }
  .pdf-corner-br { bottom: 8px; right: 8px; transform: scale(-1); }

  @media print {
    .controls, .print-hide { display: none !important; }
    body { background: white !important; }
    .app-shell { background: white !important; padding: 0 !important; }
    .cert-card { box-shadow: none !important; }
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .cert-card { animation: fadeUp 0.7s ease both; }
  .controls  { animation: fadeUp 0.7s 0.15s ease both; }
`;

const CornerSVG = () => (
  <svg viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 4 L4 28 M4 4 L28 4"
      stroke="#c9a227"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M4 4 L18 18"
      stroke="#c9a227"
      strokeWidth="1"
      strokeLinecap="round"
      opacity="0.5"
    />
    <circle cx="4" cy="4" r="2.5" fill="#c9a227" />
    <path
      d="M14 4 Q18 8 14 12 Q18 16 14 20"
      stroke="#c9a227"
      strokeWidth="1"
      fill="none"
      opacity="0.4"
    />
    <path
      d="M4 14 Q8 18 12 14 Q16 18 20 14"
      stroke="#c9a227"
      strokeWidth="1"
      fill="none"
      opacity="0.4"
    />
  </svg>
);

const DividerOrnament = () => <div className="divider-ornament">✦</div>;

/* ── Font preloader: inject a hidden span using each font ── */
function preloadFonts() {
  return new Promise((resolve) => {
    const testEl = document.createElement("div");
    testEl.setAttribute("aria-hidden", "true");
    testEl.style.cssText = `
      position:fixed; top:-999px; left:-999px; opacity:0;
      font-size:12px; white-space:nowrap; pointer-events:none;
    `;
    testEl.innerHTML = `
      <span style="font-family:'Great Vibes',cursive;">Font Test</span>
      <span style="font-family:'Cinzel',serif;">Font Test</span>
      <span style="font-family:'Cormorant Garamond',serif;">Font Test</span>
    `;
    document.body.appendChild(testEl);
    // Give fonts 1.5s to load
    setTimeout(() => {
      document.body.removeChild(testEl);
      resolve();
    }, 1500);
  });
}

export default function Certificate() {
  const defaults = {
    institution: "The Royal Institute of Advanced Learning",
    recipient: "Alexandra Morgan",
    body: "has successfully completed all required coursework, demonstrated exceptional academic merit, and fulfilled every criterion with distinction.",
    course: "Advanced Web Development & Modern JavaScript Frameworks",
    date: "March 12, 2026",
    sig1val: "Dr. J. Harrison",
    sig1name: "Dr. James R. Harrison",
    sig1role: "Director of Education",
    sig2val: "Prof. E. Vasquez",
    sig2name: "Prof. Elena Vasquez",
    sig2role: "Program Chairperson",
    credId: "CERT-2026-0312-AWD",
  };

  const [d, setD] = useState(defaults);
  const [generating, setGenerating] = useState(false);
  const pdfTargetRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const recipient = query.get("name");
    const course = query.get("course");
    const date = query.get("date");
    setD((prev) => ({
      ...prev,
      recipient: recipient || prev.recipient,
      course: course || prev.course,
      date: date || prev.date,
    }));
  }, [location.search]);

  const set = (k) => (e) => setD((p) => ({ ...p, [k]: e.target.value }));

  const Field = ({ label, k, placeholder }) => (
    <div className="mb-3">
      <div className="ctrl-label">{label}</div>
      <Input
        className="ctrl-input"
        value={d[k]}
        onChange={set(k)}
        placeholder={placeholder || label}
      />
    </div>
  );

  /* ── PDF Generation ── */
  const generatePDF = useCallback(async () => {
    if (generating) return;
    setGenerating(true);

    try {
      // 1. Ensure fonts are loaded
      await preloadFonts();

      // 2. Make target visible to html2canvas
      const target = pdfTargetRef.current;
      if (!target) throw new Error("PDF render target not found");

      target.style.visibility = "visible";
      target.style.top = "-9999px";
      target.style.left = "-9999px";

      // 3. Wait a tick for paint
      await new Promise((r) =>
        requestAnimationFrame(() => requestAnimationFrame(r)),
      );
      await new Promise((r) => setTimeout(r, 200));

      // 4. Dynamically import html2canvas
      const html2canvas = (await import("html2canvas")).default;

      const canvas = await html2canvas(target, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#fdfbf2",
        logging: false,
        width: target.offsetWidth,
        height: target.offsetHeight,
        onclone: (doc) => {
          const link = doc.createElement("link");
          link.rel = "stylesheet";
          link.href =
            "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=Cinzel:wght@400;600;700&family=Great+Vibes&display=swap";
          doc.head.appendChild(link);
        },
      });

      // 5. Hide target again
      target.style.visibility = "hidden";

      // 6. Build PDF with jsPDF
      const { jsPDF } = await import("jspdf");

      // A4 landscape: 297mm × 210mm
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pdfW = pdf.internal.pageSize.getWidth(); // 297
      const pdfH = pdf.internal.pageSize.getHeight(); // 210

      const imgData = canvas.toDataURL("image/png");

      // Exact fit — no margin, no padding
      pdf.addImage(
        imgData,
        "PNG",
        0,
        0,
        pdfW,
        pdfH,
        undefined,
        "FAST",
      );

      pdf.save(`certificate-${d.recipient.replace(/\s+/g, "-")}.pdf`);
    } catch (err) {
      console.error("PDF generation error:", err);
      alert("PDF generation failed. Please try again.");
    } finally {
      if (pdfTargetRef.current)
        pdfTargetRef.current.style.visibility = "hidden";
      setGenerating(false);
    }
  }, [d, generating]);

  /* ── Shared certificate layout (used for both preview & PDF) ── */
  const CertificateLayout = ({ isPdf = false }) => {
    const cardClass = isPdf ? "pdf-cert-card" : "cert-card";

    return (
      <div className={isPdf ? cardClass : ""}>
        {isPdf && <div className="pdf-parchment" />}

        <CertificateSvg recipient={d.recipient} course={d.course} date={d.date} />
      </div>
    );
  };

  return (
    <>
      <style>{css}</style>
      <div className="app-shell">
        {/* ── Screen preview ── */}
        <CertificateLayout isPdf={false} />

        {/* ── Hidden PDF render target (landscape, fixed width) ── */}
        <div
          ref={pdfTargetRef}
          className="pdf-render-target"
          style={{ visibility: "hidden" }}
        >
          <CertificateSvg recipient={d.recipient} course={d.course} date={d.date} />
        </div>
        <div className="controls">
          <div className="mt-4 d-flex gap-3 align-items-center flex-wrap">
            <button
              className="btn-gold"
              onClick={generatePDF}
              disabled={generating}
            >
              {generating ? (
                <>
                  <span
                    style={{
                      display: "inline-block",
                      width: 14,
                      height: 14,
                      border: "2px solid #0e0c08",
                      borderTopColor: "transparent",
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                    }}
                  />
                  Generating…
                </>
              ) : (
                <>📄 Download PDF Certificate</>
              )}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
   EXPORTED UTILITY — called from Quiz.jsx to generate a PDF
   Blob without triggering a download.
───────────────────────────────────────────────────────────── */
export async function generateCertificatePDFBlob({ recipient, course }) {
  // 1. Build a temporary container off-screen
  const container = document.createElement("div");
  container.style.cssText = `
    position: fixed;
    top: -9999px;
    left: -9999px;
    width: 1122px;
    background: #fdfbf2;
    visibility: visible;
    z-index: -1;
    pointer-events: none;
  `;
  document.body.appendChild(container);

  // 2. Inject Google Fonts into the document (if not already present)
  if (!document.querySelector("#cert-font-link")) {
    const link = document.createElement("link");
    link.id = "cert-font-link";
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=Cinzel:wght@400;600;700&family=Great+Vibes&display=swap";
    document.head.appendChild(link);
  }

  // 3. Build the certificate HTML with inline styles (same as PDF render target)
  const d = {
    institution: "The Royal Institute of Advanced Learning",
    recipient: recipient || "Graduate",
    body: "has successfully completed all required coursework, demonstrated exceptional academic merit, and fulfilled every criterion with distinction.",
    course: course || "",
    date: new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }),
    sig1val: "Dr. J. Harrison",
    sig1name: "Dr. James R. Harrison",
    sig1role: "Director of Education",
    sig2val: "Prof. E. Vasquez",
    sig2name: "Prof. Elena Vasquez",
    sig2role: "Program Chairperson",
    credId: `CERT-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`,
  };

  // 4. Use ReactDOM to render the CertificateSvg component into the container
  const { createRoot } = await import("react-dom/client");
  const React = await import("react");

  // 5. Mount CertificateSvg (same as CertificateLayout), wait for fonts, then capture
  const root = createRoot(container);
  root.render(React.createElement(CertificateSvg, { recipient: d.recipient, course: d.course, date: d.date }));

  await preloadFonts();
  await new Promise((r) =>
    requestAnimationFrame(() => requestAnimationFrame(r)),
  );
  await new Promise((r) => setTimeout(r, 300));

  try {
    const html2canvas = (await import("html2canvas")).default;
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      backgroundColor: "#fdfbf2",
      logging: false,
      width: container.offsetWidth,
      height: container.offsetHeight,
      onclone: (doc) => {
        const link = doc.createElement("link");
        link.rel = "stylesheet";
        link.href =
          "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=Cinzel:wght@400;600;700&family=Great+Vibes&display=swap";
        doc.head.appendChild(link);
      },
    });

    const { jsPDF } = await import("jspdf");
    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
      compress: true,
    });

    const pdfW = pdf.internal.pageSize.getWidth();
    const pdfH = pdf.internal.pageSize.getHeight();
    const margin = 5;
    const imgW = pdfW - margin * 2;
    const imgH = (canvas.height / canvas.width) * imgW;
    const yOffset = (pdfH - imgH) / 2;

    pdf.addImage(
      canvas.toDataURL("image/png"),
      "PNG",
      margin,
      yOffset,
      imgW,
      imgH,
      undefined,
      "FAST",
    );

    // With:
    const rawBlob = pdf.output("blob");
    return new Blob([rawBlob], { type: "application/pdf" });
  } finally {
    root.unmount();
    document.body.removeChild(container);
  }
}
